import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Link,
  Tabs,
  Tab,
  Divider
} from '@mui/material';
import { Google as GoogleIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { COLORS } from '../../theme/colors';

const LoginModal = ({ open, onClose, onSuccessCallback }) => {
  const [tab, setTab] = useState(0); // 0 for login, 1 for register
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  
  const { login, register, signInWithGoogle } = useAuth();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
    setError('');
    setFormData({
      email: '',
      password: '',
      name: '',
      confirmPassword: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (tab === 0) {
        // Login
        await login(formData.email, formData.password);
      } else {
        // Register
        if (formData.password !== formData.confirmPassword) {
          throw new Error('Passwords do not match');
        }
        if (formData.password.length < 6) {
          throw new Error('Password must be at least 6 characters');
        }
        await register(formData.email, formData.password, formData.name);
      }
      
      // Call success callback if provided
      if (onSuccessCallback) {
        await onSuccessCallback();
      }
      
      onClose();
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError('');
    setFormData({
      email: '',
      password: '',
      name: '',
      confirmPassword: ''
    });
    onClose();
  };

  const handleGoogleAuth = async () => {
    setGoogleLoading(true);
    setError('');
    
    try {
      await signInWithGoogle();
      
      // Call success callback if provided
      if (onSuccessCallback) {
        await onSuccessCallback();
      }
      
      onClose();
    } catch (error) {
      setError(error.message);
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" component="div">
          {tab === 0 ? 'Login to Request Quote' : 'Create Account'}
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={tab} onChange={handleTabChange}>
            <Tab label="Login" />
            <Tab label="Register" />
          </Tabs>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          {tab === 1 && (
            <TextField
              fullWidth
              label="Full Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              margin="normal"
              required
            />
          )}
          
          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            margin="normal"
            required
          />
          
          <TextField
            fullWidth
            label="Password"
            name="password"
            type="password"
            value={formData.password}
            onChange={handleChange}
            margin="normal"
            required
          />
          
          {tab === 1 && (
            <TextField
              fullWidth
              label="Confirm Password"
              name="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={handleChange}
              margin="normal"
              required
            />
          )}

          {tab === 0 && (
            <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
              Don't have an account?{' '}
              <Link
                component="button"
                type="button"
                onClick={() => setTab(1)}
                sx={{ color: COLORS.primary.main }}
              >
                Sign up here
              </Link>
            </Typography>
          )}

          {tab === 1 && (
            <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
              Already have an account?{' '}
              <Link
                component="button"
                type="button"
                onClick={() => setTab(0)}
                sx={{ color: COLORS.primary.main }}
              >
                Login here
              </Link>
            </Typography>
          )}
        </Box>

        {/* Google Authentication */}
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              OR
            </Typography>
          </Divider>
          
          <Button
            fullWidth
            variant="outlined"
            startIcon={googleLoading ? <CircularProgress size={16} /> : <GoogleIcon />}
            onClick={handleGoogleAuth}
            disabled={loading || googleLoading}
            sx={{
              py: 1.5,
              borderColor: '#dadce0',
              color: '#3c4043',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 500,
              '&:hover': {
                borderColor: '#dadce0',
                backgroundColor: '#f8f9fa'
              }
            }}
          >
            {googleLoading ? 'Signing in...' : `${tab === 0 ? 'Sign in' : 'Sign up'} with Google`}
          </Button>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          sx={{ 
            backgroundColor: COLORS.primary.main,
            '&:hover': {
              backgroundColor: COLORS.primary.dark
            }
          }}
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            tab === 0 ? 'Login' : 'Create Account'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LoginModal;
